import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

const KowriCallbackRedirect = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing payment callback...');

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Extract callback parameters from URL
        const callbackParams = Object.fromEntries(searchParams.entries());

        console.log('Kowri callback received:', callbackParams);

        // Call the Edge Function with proper authorization
        const { data, error } = await supabase.functions.invoke('payment-callback', {
          body: callbackParams
        });

        if (error) {
          throw error;
        }

        console.log('Edge Function response:', data);

        setStatus('success');
        setMessage('Payment callback processed successfully');

        // Redirect to appropriate success/failure page based on status
        const paymentStatus = callbackParams.status;
        const orderNumber = callbackParams.cust_ref?.split('-').slice(0, 2).join('-') || 'Unknown';

        setTimeout(() => {
          if (paymentStatus === '0') {
            // Payment successful - redirect to success page
            window.location.replace(`/payment-success?status=success&orderNumber=${orderNumber}&transactionId=${callbackParams.transaction_id}`);
          } else if (paymentStatus === '-2') {
            // Payment cancelled - redirect to cancelled page
            window.location.replace(`/payment-cancelled?orderNumber=${orderNumber}`);
          } else if (paymentStatus === '-1') {
            // Payment failed - redirect to failure page
            window.location.replace(`/payment-error?orderNumber=${orderNumber}&message=${callbackParams.reason || 'Payment failed'}`);
          } else {
            // Unknown status - redirect to generic page
            window.location.replace(`/payment-error?orderNumber=${orderNumber}&message=Unknown status`);
          }
        }, 2000); // 2 second delay to show processing message

      } catch (error) {
        console.error('Error processing Kowri callback:', error);
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'Unknown error occurred');
      }
    };

    processCallback();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
        <div className="mb-4">
          {status === 'processing' && (
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          )}
          {status === 'success' && (
            <div className="text-green-600">
              <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          {status === 'error' && (
            <div className="text-red-600">
              <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          )}
        </div>

        <h2 className="text-xl font-semibold mb-2">
          {status === 'processing' && 'Processing Payment Callback'}
          {status === 'success' && 'Callback Processed'}
          {status === 'error' && 'Processing Failed'}
        </h2>

        <p className="text-gray-600">{message}</p>

        {import.meta.env.DEV && (
          <div className="mt-6 text-xs text-gray-500 border-t pt-4">
            <p><strong>Debug Info:</strong></p>
            <pre className="text-left bg-gray-100 p-2 rounded mt-2 overflow-auto">
              {JSON.stringify(Object.fromEntries(searchParams.entries()), null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default KowriCallbackRedirect;